import React, { useEffect } from 'react';
import { useUID } from 'react-uid';

const DATA_ATTR = {
	TRACK_WITHIN_CONTAINER_ID: 'data-analytics-track-within',
	ANALYTICS_TARGET_ID: 'data-analytics-id',
};

export type TTrackingCallbackParams = {
	text: string;
	link: string;
};

export type TTargetLinksCallback = [
	string,
	(props: TTrackingCallbackParams) => void,
][];

interface IProps {
	targetLinks: TTargetLinksCallback;
	children: React.ReactNode;
}
/**
 * Bind tracking events to target links that will exist somewhere
 * amongst the children provided to this component.
 *
 * Useful for when hyperlinks or other clickable content is
 * not explicitly controllable (e.g. in CMS richtext).
 */
const TrackLinksWithin = ({ targetLinks, children }: IProps) => {
	// Unique ID generated for the container div wrapping the provided
	// children. This is necessary to limit the scope of the below
	// `querySelectorAll` event bind to just the children provided
	const trackWithinId = useUID();

	// Ensure component has mounted
	useEffect(() => {
		targetLinks.forEach(link => {
			const [analyticsId, trackingCallback] = link;

			// `querySelectorAll` to potentially track multiple links
			// that share the same `DATA_ATTR.ANALYTICS_TARGET_ID` attribute value
			const targetElements = document.querySelectorAll<HTMLElement>(
				`[${DATA_ATTR.TRACK_WITHIN_CONTAINER_ID}="${trackWithinId}"] [${DATA_ATTR.ANALYTICS_TARGET_ID}="${analyticsId}"]`,
			);

			targetElements.forEach(el => {
				const text = el.textContent as string;
				const link = el.getAttribute('href') as string;

				// Note that the actual `trackEvent` analytics function
				// call still belongs in the implementing component, passed
				// into the `targetLinks` props tuple array
				el.addEventListener('click', () => trackingCallback({ text, link }));
			});
		});
	}, [targetLinks, trackWithinId, children]);

	// We're using a data attribute with unique value on the container
	// here instead of an ID so we can chain it as a valid selector above
	// in `querySelectorAll`; using an `id` attribute would make an invalid selector
	return (
		<div
			{...{
				[DATA_ATTR.TRACK_WITHIN_CONTAINER_ID]: trackWithinId,
			}}
		>
			{children}
		</div>
	);
};

export default TrackLinksWithin;
